'use client';

import { ValidationError } from '@eliadmin/core/src/api/errors';
import { sendContactMessage } from '@eliadmin/core/src/api/forms/forms-api';

import { Button } from '@eliadmin/core/src/components/ui/button';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import { CONTACT_CONFIG } from '@eliadmin/core/src/constants/contact-config';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import Link from '@eliadmin/core/src/components/link';
import { useRouter } from 'next/navigation';

import { useForm } from 'react-hook-form';
import { type z } from 'zod';
import { object as __eliadmin_zod_0, string as __eliadmin_zod_1, boolean as __eliadmin_zod_2 } from 'zod';
import { ensureZodDefaults as __eliadmin_zod_defaults } from '@eliadmin/core/src/build/zod-defaults';
__eliadmin_zod_defaults();
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { getDefaultUrlForTosOrGdprLocalized } from '@eliadmin/core/src/utils/misc-utils';
import { toast } from '@eliadmin/core/src/components/ui/use-toast';
import { Field } from '../form/field';

const customTourSchema = (VM: FormValidationMessages) =>
  __eliadmin_zod_0({
    name: __eliadmin_zod_1().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    email: __eliadmin_zod_1().email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()),
    phone: __eliadmin_zod_1().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    destination: __eliadmin_zod_1().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    passengers: __eliadmin_zod_1().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    term: __eliadmin_zod_1().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    gdpr: __eliadmin_zod_2().refine(val => val === true, { message: VM.GDPR_VALIDATION_MESSAGE() }),
    'g-recaptcha-response': __eliadmin_zod_1().default(''),
  });

export type CustomTourFormValues = z.infer<ReturnType<typeof customTourSchema>>;

export function CustomTourForm() {
  const { t } = useTranslations();
  const { formValidationMessages } = useFormValidationMessages(t);

  const form = useForm<CustomTourFormValues>({
    resolver: zodResolver(customTourSchema(formValidationMessages)),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      destination: '',
      passengers: '',
      term: '',
      gdpr: false,
    },
  });

  const { mutateAsync } = useMutation({
    mutationFn: sendContactMessage,
  });

  const locale = useLocaleStore(s => s.currentLocale);
  const router = useRouter();

  const handleSubmit = form.handleSubmit(async data => {
    try {

      await mutateAsync({
        data: {
          name: data.name,
          email: data.email,
          phone: data.phone,
          gdpr: data.gdpr,
          custom: {
            ['Destinácia']: data.destination,
            ['Počet osôb']: data.passengers,
            ['Preferovaný termín']: data.term,
          },

        },
        locale,
      });

      toast({
        title: t('text3313'),
        variant: 'success',
      });

      router.push(ROUTES.HOME());
    } catch (err) {
      if (err instanceof ValidationError) {
        for (const [field, errors] of Object.entries(err.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        return;
      }

      toast({
        title: t('text657'),
        description: (err as any)?.message,
        variant: 'destructive',
      });
    }
  });

  return (
    <Form {...form}>
      <form
        onSubmit={handleSubmit}
        className={cn('grid w-full grid-cols-12 gap-4 @container', 'ea-custom-tour-form')}
      >
        <Field
          required
          control={form.control}
          name="name"
          label={t('text33')}
          placeholder={t('text33')}
          className={{ wrapper: 'col-span-12 sm:col-span-6' }}
        />

        <Field
          required
          control={form.control}
          name="phone"
          label={t('text36')}
          type="tel"
          placeholder={t('text36')}
          className={{ wrapper: 'col-span-12 sm:col-span-6' }}
        />

        <Field
          required
          control={form.control}
          name="email"
          type="email"
          label={t('text35')}
          placeholder={t('text35')}
          className={{ wrapper: 'col-span-12 sm:col-span-6' }}
        />

        <Field
          required
          control={form.control}
          name="destination"
          label={t('text3309')}
          placeholder={t('text3309')}
          className={{ wrapper: 'col-span-12 sm:col-span-6' }}
        />

        <Field
          required
          control={form.control}
          name="passengers"
          label={t('text2512')}
          placeholder={t('text3310') + '?'}
          className={{ wrapper: 'col-span-12 sm:col-span-6' }}
        />

        <Field
          required
          control={form.control}
          name="term"
          label={t('text3312')}
          placeholder={t('text3311') + '?'}
          className={{ wrapper: 'col-span-12 sm:col-span-6' }}
        />

        <FormField
          control={form.control}
          name="gdpr"
          render={({ field }) => (
            <FormItem className="col-span-12 flex items-center gap-2">
              <FormControl>
                <Checkbox
                  ref={field.ref}
                  name={field.name}
                  disabled={field.disabled}
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <FormLabel className="text-sm font-normal text-foreground sm:text-sm">
                {t('text613')}{' '}
                <Link
                  href={getDefaultUrlForTosOrGdprLocalized(ROUTES?.GDPR?.() ?? '/', locale, 'gdpr')}
                  prefetch={false}
                >
                  <span className="text-foreground underline">{t('text614')}</span>
                </Link>
              </FormLabel>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name={'g-recaptcha-response'}
          render={() => (
            <FormItem required className="col-span-12">
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="col-span-12 flex w-full items-center justify-center">
          <Button
            type="submit"
            variant={CONTACT_CONFIG.form.submit.variant}
            className="h-[52px] w-full min-w-[300px] sm:w-fit"
            isLoading={form.formState.isSubmitting}
          >
            {t('text185')}
          </Button>
        </div>
      </form>
    </Form>
  );
}
